import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Sparkles, QrCode, Radio, MapPin, Map as MapIcon, Share2, PartyPopper,
  ChevronLeft, ChevronRight, X,
} from 'lucide-react';

/**
 * One-time post-signup tour for new Thirrsty users.
 *
 * Behavior:
 *  - Renders nothing if no logged-in user OR if user.has_seen_welcome is true.
 *  - Silent, text-only tour (no narration).
 *  - On Finish or Skip, calls POST /api/users/me/welcome-complete and refreshes
 *    the auth context so the tour never opens again.
 */

const TOUR_SLIDES = [
  {
    id: 'welcome',
    icon: Sparkles,
    iconBg: 'from-[#F6B24A] to-[#FF7E5F]',
    title: 'Welcome to Thirrsty.com',
    subtitle: 'Your nightlife companion',
    body:
      "Hey there! Welcome to Thirrsty.com — your free pass to the best nightlife in the country. Bars, clubs, lounges, coffee shops, vineyards, breweries, and even frat parties. Here's the quick tour.",
  },
  {
    id: 'qr',
    icon: QrCode,
    iconBg: 'from-[#F6B24A] to-[#FFC065]',
    title: 'Your Personal QR Code',
    subtitle: 'Scan it. Save money.',
    body:
      "Every Thirrsty profile comes with its own unique QR code. Tap the Profile tab to see yours. Show it at any partner venue — the bartender scans it, and you get instant discounts on drinks. We even check you in automatically so your friends can see where you're at.",
  },
  {
    id: 'timeline',
    icon: Radio,
    iconBg: 'from-red-500 to-[#FF7E5F]',
    title: 'Timeline & Go Live',
    subtitle: 'See the scene in real time',
    body:
      "Your Home screen shows a live timeline of posts and stories from the Thirrsty community. When you walk into a venue, tap the red Go Live button — your crew will see exactly where you're hanging out tonight.",
  },
  {
    id: 'discover',
    icon: MapPin,
    iconBg: 'from-[#2DE2E6] to-[#7DF9FF]',
    title: 'Discover Nightlife',
    subtitle: 'Find your next spot',
    body:
      "Tap the Discover tab to browse venues by category. Looking for a bar? A vineyard? A late-night coffee shop? It's all here. You can also type in any U.S. zip code to find spots right around your location.",
  },
  {
    id: 'map',
    icon: MapIcon,
    iconBg: 'from-[#F6B24A] to-red-500',
    title: 'The Thirrsty Map',
    subtitle: 'Pins across the USA',
    body:
      "Open the Map tab to see every Thirrsty venue across the country, marked with red pulsating pins. Tap any pin to jump into that venue's page, see their hours, and grab your discount.",
  },
  {
    id: 'share',
    icon: Share2,
    iconBg: 'from-[#F6B24A] to-[#2DE2E6]',
    title: 'Bring Your Crew',
    subtitle: 'Share Thirrsty with friends',
    body:
      "Got friends who love the nightlife? Tap the share icon at the top of the Home screen to invite them. They get free profiles and free discounts too — and the more friends you bring, the better the vibes.",
  },
  {
    id: 'done',
    icon: PartyPopper,
    iconBg: 'from-[#F6B24A] to-[#FF7E5F]',
    title: "You're All Set",
    subtitle: "Let's go enjoy the night",
    body:
      "And that's it! You're officially part of the Thirrsty family. Go grab your QR code, find a spot, and have a great time. Cheers — welcome to the nightlife!",
  },
];

export function WelcomeTour({ forceOpen = false, onClose }) {
  const { user, refresh } = useAuth();
  const [index, setIndex] = useState(0);
  const [closing, setClosing] = useState(false);

  const isOpen = forceOpen
    ? Boolean(user) && !closing
    : Boolean(user) && user.has_seen_welcome === false && !closing;
  const slide = TOUR_SLIDES[index];
  const isLast = index === TOUR_SLIDES.length - 1;

  const goNext = () => {
    if (index < TOUR_SLIDES.length - 1) setIndex((i) => i + 1);
  };
  const goPrev = () => {
    if (index > 0) setIndex((i) => i - 1);
  };

  const finishTour = async () => {
    if (forceOpen) {
      setIndex(0);
      onClose?.();
      return;
    }
    setClosing(true);
    try {
      await api.post('/users/me/welcome-complete');
      await refresh();
    } catch (e) {
      // Even if the API call fails, hide the tour locally so the user is not stuck.
      // refresh() will retry on next mount.
    }
  };

  const Icon = slide?.icon || Sparkles;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/85 backdrop-blur-md p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-tour-title"
      data-testid="welcome-tour-overlay"
    >
      <div className="relative w-full max-w-md rounded-3xl border border-white/12 bg-gradient-to-b from-[#0E0E14] to-[#07070A] shadow-2xl shadow-black/60 overflow-hidden th-float-in">
        {/* Skip */}
        <div className="absolute top-3 right-3 z-10 flex items-center gap-1">
          <button
            type="button"
            onClick={finishTour}
            aria-label="Skip the welcome tour"
            className="inline-flex items-center gap-1 rounded-full bg-white/5 px-3 py-1.5 text-xs text-white/75 hover:bg-white/10 transition-colors"
            data-testid="welcome-tour-skip-button"
          >
            <X className="h-3.5 w-3.5" /> Skip
          </button>
        </div>

        {/* Slide */}
        <div className="px-7 pt-12 pb-5 text-center">
          <div className={`mx-auto mb-5 grid h-20 w-20 place-items-center rounded-3xl bg-gradient-to-br ${slide.iconBg} text-[#1A1208] shadow-lg shadow-black/40`}>
            <Icon className="h-9 w-9" strokeWidth={2.25} />
          </div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#F6B24A]/85">{slide.subtitle}</p>
          <h2
            id="welcome-tour-title"
            className="mt-1 font-display text-2xl font-semibold text-white"
            style={{ fontFamily: 'Space Grotesk' }}
            data-testid={`welcome-tour-title-${slide.id}`}
          >
            {slide.title}
          </h2>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-white/75" data-testid={`welcome-tour-body-${slide.id}`}>
            {slide.body}
          </p>
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 py-3">
          {TOUR_SLIDES.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Go to step ${i + 1}: ${s.title}`}
              data-testid={`welcome-tour-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-6 bg-[#F6B24A]' : 'w-1.5 bg-white/25 hover:bg-white/40'
              }`}
            />
          ))}
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between gap-3 border-t border-white/8 bg-white/[0.02] px-5 py-4">
          <Button
            type="button"
            variant="ghost"
            onClick={goPrev}
            disabled={index === 0}
            className="rounded-full text-white/75 hover:bg-white/10 disabled:opacity-30"
            data-testid="welcome-tour-prev-button"
          >
            <ChevronLeft className="mr-1 h-4 w-4" /> Back
          </Button>

          {!isLast ? (
            <Button
              type="button"
              onClick={goNext}
              className="rounded-full bg-[#F6B24A] px-5 text-[#1A1208] hover:bg-[#FFC065] font-semibold"
              data-testid="welcome-tour-next-button"
            >
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={finishTour}
              className="rounded-full bg-[#F6B24A] px-5 text-[#1A1208] hover:bg-[#FFC065] font-semibold"
              data-testid="welcome-tour-finish-button"
            >
              Let's go <PartyPopper className="ml-1.5 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default WelcomeTour;
